import { supabase } from '@/integrations/supabase/client';

export type InvokeResult<T> = { data: T | null; error: InvokeError | null; requestId?: string };

/** Normalized error codes for edge invocations. */
export const INVOKE_ERROR_CODES = {
  NOT_AUTHENTICATED: 'NOT_AUTHENTICATED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  EDGE_ERROR: 'EDGE_ERROR',
} as const;

export type InvokeErrorCode =
  | (typeof INVOKE_ERROR_CODES)[keyof typeof INVOKE_ERROR_CODES];

export interface InvokeError {
  ok: false;
  code: InvokeErrorCode;
  message: string;
  requestId?: string;
  edgeCode?: string;
}

function toInvokeError(
  code: InvokeErrorCode,
  message?: string,
  requestId?: string,
  edgeCode?: string
): InvokeError {
  return { ok: false, code, message: message ?? code, requestId, edgeCode };
}

/** JWT-shaped check: 3 dot-separated parts, each >10 chars. Do not use startsWith('eyJ'). */
function looksLikeJwt(t: string): boolean {
  const parts = t.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 10);
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Option C: keep Authorization, never send x-request-id on invoke. */
export function buildAuthedEdgeHeaders(
  token: string,
  extra?: Record<string, string>
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(extra ?? {}),
  };
  delete headers['x-request-id'];
  return headers;
}

/** Thrown when session is missing (do not invoke edge without auth). */
export class InvokeNotAuthenticatedError extends Error {
  constructor(
    message: string = 'Not authenticated',
    public requestId?: string
  ) {
    super(message);
    this.name = 'InvokeNotAuthenticatedError';
  }
}

export async function invokeAuthedEdge<T>(
  fnName: string,
  body: unknown,
  options?: { headers?: Record<string, string>; route?: string }
): Promise<InvokeResult<T>> {
  const requestId = generateRequestId();

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  const rawToken = sessionData?.session?.access_token;
  const hasSession = !!(sessionData?.session);
  const token =
    typeof rawToken === 'string' && rawToken.length > 50 && looksLikeJwt(rawToken)
      ? rawToken
      : null;

  if (sessionError || !hasSession || !token) {
    const err = new InvokeNotAuthenticatedError('Not authenticated', requestId);
    console.error('[invokeAuthedEdge] requestId=%s not authenticated', requestId);
    throw err;
  }

  // Keep requestId for local logs/toasts; do not send x-request-id.
  const headers = buildAuthedEdgeHeaders(token, options?.headers);

  try {
    const res = await supabase.functions.invoke(fnName, { body, headers });
    const resError = (res as { error?: { context?: { status?: number } }; data?: unknown }).error;
    const resData = (res as { data?: { ok?: boolean; error?: { code?: string; message?: string; requestId?: string } } }).data;

    if (resError) {
      const status = resError?.context?.status;
      const code =
        status === 401
          ? INVOKE_ERROR_CODES.UNAUTHORIZED
          : status === 403
            ? INVOKE_ERROR_CODES.FORBIDDEN
            : INVOKE_ERROR_CODES.EDGE_ERROR;
      const edgeErr = resData && typeof resData === 'object' && 'error' in resData && resData.error && typeof resData.error === 'object'
        ? (resData as { error: { code?: string; message?: string; requestId?: string } }).error
        : null;
      const message = edgeErr?.message ?? code;
      const rid = edgeErr?.requestId ?? requestId;
      return {
        data: null,
        error: toInvokeError(code, message, rid, edgeErr?.code),
        requestId: rid,
      };
    }

    if (resData && typeof resData === 'object' && (resData as { ok?: boolean }).ok === false) {
      const edgeErr = (resData as { error?: { code?: string; message?: string; requestId?: string } }).error;
      const message = edgeErr?.message ?? "EDGE_ERROR";
      const rid = edgeErr?.requestId ?? requestId;
      return {
        data: null,
        error: toInvokeError(INVOKE_ERROR_CODES.EDGE_ERROR, message, rid, edgeErr?.code),
        requestId: rid,
      };
    }

    return { data: (res as { data: T | null }).data ?? null, error: null, requestId };
  } catch (e) {
    if (e instanceof InvokeNotAuthenticatedError) throw e;
    console.error('[invokeAuthedEdge] requestId=%s exception', requestId, e);
    return {
      data: null,
      error: toInvokeError(INVOKE_ERROR_CODES.EDGE_ERROR, undefined, requestId),
      requestId,
    };
  }
}
